import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brain, Clock, Sparkles, FileText } from "lucide-react";

/**
 * Landing-page promo block for the MoneyMind™ Quiz. Sits between
 * `CalendarPreview` and `Features` so visitors who aren't ready to sign up
 * have a 5-minute, zero-friction discovery moment that ends on their persona
 * + a soft signup CTA. Also doubles as a powerful lead-gen surface — the
 * quiz works fully anonymously and the result page funnels into /register.
 *
 * Design echoes the quiz page itself: gold accent pill ("PERSONALITY ASSESSMENT"),
 * serif headline with ™, meta-row of 3 trust signals, gold pill CTA.
 */
export default function QuizPromo() {
  return (
    <section
      className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28"
      data-testid="landing-quiz-promo"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Left — copy */}
        <div className="lg:col-span-7">
          <span
            className="inline-block px-4 py-1.5 rounded-full text-[11px] font-semibold tracking-widest uppercase mb-6"
            style={{ background: "#FAEACB", color: "#A9712A" }}
            data-testid="landing-quiz-eyebrow"
          >
            Personality Assessment
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-light leading-[1.05] tracking-tight">
            What kind of <em className="not-italic" style={{ color: "#A9712A" }}>MoneyMind</em>
            <br />
            are you?
          </h2>
          <p className="mt-7 text-lg text-[#4d4f4c] max-w-xl leading-relaxed">
            Twenty questions. About five minutes. On the other side, you'll get
            your MoneyMind&trade; persona — a snapshot of your strengths, growth
            edges, and three habits tuned to how you actually think about money.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#4d4f4c]">
            <span className="inline-flex items-center gap-1.5">
              <FileText className="h-4 w-4" style={{ color: "#D49B3C" }} />
              20 questions
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" style={{ color: "#D49B3C" }} />
              ~5 minutes
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Brain className="h-4 w-4" style={{ color: "#D49B3C" }} />
              8 unique personas
            </span>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/quiz"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold transition-opacity hover:opacity-90"
              style={{ background: "#D49B3C", color: "#2A1A05" }}
              data-testid="landing-quiz-cta"
            >
              <Sparkles className="h-4 w-4" />
              Discover My MoneyMind
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <span className="text-xs text-[#787671] italic">
              No account required · Get your result instantly
            </span>
          </div>
        </div>

        {/* Right — persona-card visual collage */}
        <div className="lg:col-span-5">
          <PersonaCollage />
        </div>
      </div>
    </section>
  );
}


/**
 * Decorative 3-card stack showing 3 of the 8 personas. Each card is the
 * same visual language used on the actual result reveal screen so visitors
 * get a preview of what they'll see if they take the quiz.
 *
 * We hand-pick three personas that span the trait spectrum — Architect
 * (planner), Explorer (risk-taker), Connector (relational) — to broadcast
 * "you'll see yourself in one of these." Persona colors come from the
 * canonical PERSONAS palette in `quiz_data.py`.
 */
function PersonaCollage() {
  const cards = [
    { icon: "🏛️", name: "The Architect", tagline: "You don't dream about wealth — you engineer it.", color: "#1B3A6B", rotate: "-6deg", offset: "0px" },
    { icon: "🧭", name: "The Explorer",  tagline: "Money is your map.",                             color: "#1F6F8B", rotate: "3deg",  offset: "20px" },
    { icon: "🤝", name: "The Connector", tagline: "Your generosity is your legacy.",                color: "#D49B3C", rotate: "-2deg", offset: "10px" },
  ];
  return (
    <div className="relative h-[420px] sm:h-[460px]" aria-hidden>
      {cards.map((c, i) => (
        <div
          key={c.name}
          className="absolute left-0 right-0 mx-auto p-5 text-white shadow-md max-w-xs"
          style={{
            background: c.color,
            transform: `rotate(${c.rotate}) translateY(${c.offset})`,
            top: `${i * 100}px`,
            zIndex: 3 - i,
          }}
        >
          <p className="text-[10px] uppercase tracking-widest opacity-70">
            Your MoneyMind&trade;
          </p>
          <p className="font-serif text-2xl mt-2 flex items-center gap-2">
            <span>{c.icon}</span>
            {c.name}
          </p>
          <p className="font-serif italic text-sm mt-2 opacity-90">"{c.tagline}"</p>
        </div>
      ))}
    </div>
  );
}
